import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Table, TableBody, TableCell, TableRow, Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ENV from "../../../config/env";
import { anularSolicitud, aprobarSolicitudes, fmtFecha, fmtMoney, getSolicitud } from "../services/egresosService";

const cellSx = { py: 0.3, px: 0.8, fontSize: 12 };

const Fila = ({ items }) => (
  <TableRow>
    {items.flatMap(([etq, val, span], i) => [
      <TableCell key={`e${i}`} sx={{ ...cellSx, fontWeight: 800, whiteSpace: "nowrap", width: 140 }}>{etq}</TableCell>,
      <TableCell key={`v${i}`} sx={cellSx} colSpan={span || 1}>{val}</TableCell>,
    ])}
  </TableRow>
);

// Comprobante de una solicitud de cheque (espejo de imp_comprobante2_modal.php). Desde aquí se
// puede aprobar (si está en espera) o anular (si todavía no se emitió).
export default function SolicitudDialog({ id, onClose, onCambio, onModificar }) {
  const [s, setS] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [procesando, setProcesando] = useState(false);

  useEffect(() => {
    if (!id) { setS(null); return; }
    setError("");
    setCargando(true);
    getSolicitud(id)
      .then((r) => setS(r.data))
      .catch((e) => setError(e?.response?.data?.error || "No se pudo cargar la solicitud."))
      .finally(() => setCargando(false));
  }, [id]);

  const accion = async (fn, confirmar) => {
    if (!window.confirm(confirmar)) return;
    setProcesando(true);
    setError("");
    try {
      await fn();
      if (onCambio) onCambio();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo completar la acción.");
    } finally {
      setProcesando(false);
    }
  };

  return (
    <Dialog open={!!id} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>Comprobante de solicitud de cheque N° {id}</DialogTitle>
      <DialogContent dividers>
        {cargando && <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={22} /></Box>}
        {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
        {s && (
          <>
            <Table size="small" sx={{ border: 1, borderColor: "divider", mb: 1 }}>
              <TableBody>
                <Fila items={[["Fecha de registro", fmtFecha(s.fecha)], ["Tipo de cambio", s.cambio]]} />
                <Fila items={[["Dirigido a", s.nombre, 3]]} />
                <Fila items={[["Depósito a", s.deposito, 3]]} />
                <Fila items={[["N° de cheque", s.nocheque], ["N° de contrato", s.nro_contrato]]} />
                <Fila items={[["Ciudad destino", s.ciudad], ["N° de referencia", s.nro_referencia]]} />
                <Fila items={[["Banco", s.banco], ["Tipo de servicio", s.servicio]]} />
                <Fila items={[["Moneda", s.moneda], ["Tipo de egreso", s.tipo_retencion]]} />
                <Fila items={[["Importe en Bs", fmtMoney(s.bolivianos)], ["Importe en $us", fmtMoney(s.dolares)]]} />
                {s.importe_total !== null && <Fila items={[["Importe total", fmtMoney(s.importe_total)], ["Retención", fmtMoney(s.retencion)]]} />}
                <Fila items={[["Concepto", s.concepto], ["N° factura", s.facturas.map((f) => f.nro_factura).filter(Boolean).join(", ") || s.nro_factura]]} />
                <Fila items={[["Mes de servicio", s.mes, 3]]} />
                <Fila items={[["Observación", s.obs, 3]]} />
                <Fila items={[["Literal", s.literal, 3]]} />
                <Fila items={[["Cliente", s.cliente, 3]]} />
                <Fila items={[["Solicitante", s.usu], ["Correo", s.correo]]} />
                <Fila items={[["Estado", s.estado, 3]]} />
              </TableBody>
            </Table>
            {s.archivos?.length > 0 && (
              <>
                <Typography sx={{ fontSize: 12, fontWeight: 800, mb: 0.5 }}>Archivos adjuntos ({s.archivos.length})</Typography>
                <Box sx={{ border: 1, borderColor: "divider", p: 0.75, mb: 1, display: "grid", gap: 0.4 }}>
                  {s.archivos.map((a) => (
                    <Box key={a.url} sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 12 }}>
                      <AttachFileIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                      <Link href={`${ENV.API_BASE_URL}${a.url}`} target="_blank" rel="noopener noreferrer">{a.nombre}</Link>
                    </Box>
                  ))}
                </Box>
              </>
            )}
            {s.orden_compra && (
              <>
                <Typography sx={{ fontSize: 12, fontWeight: 800, mb: 0.5 }}>Orden de compra enlazada</Typography>
                <Table size="small" sx={{ border: 1, borderColor: "divider" }}>
                  <TableBody>
                    <Fila items={[["N° de factura", s.orden_compra.num_factura], ["Tipo", s.orden_compra.tipo]]} />
                    <Fila items={[["Forma de pago", s.orden_compra.forma_pago], ["Monto", fmtMoney(s.orden_compra.monto)]]} />
                    <Fila items={[["Moneda", s.orden_compra.moneda], ["Costo de envío", fmtMoney(s.orden_compra.costo_envio)]]} />
                    <Fila items={[["Orden proceso", s.orden_compra.orden_proceso], ["Precio unitario", fmtMoney(s.orden_compra.precio_unitario)]]} />
                  </TableBody>
                </Table>
              </>
            )}
            {s.facturas.length > 0 && (
              <>
                <Typography sx={{ fontSize: 12, fontWeight: 800, mt: 1, mb: 0.5 }}>Facturas</Typography>
                <Table size="small" sx={{ border: 1, borderColor: "divider" }}>
                  <TableBody>
                    {s.facturas.map((f) => (
                      <TableRow key={f.id}>
                        <TableCell sx={cellSx}>{f.nro_factura}</TableCell><TableCell sx={cellSx}>{fmtFecha(f.fecha_factura)}</TableCell>
                        <TableCell sx={cellSx}>{f.denominacion}</TableCell><TableCell sx={cellSx} align="right">{fmtMoney(f.importe_total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </>
        )}
      </DialogContent>
      {/* Anular (destructivo) queda aparte a la izquierda; a la derecha Cerrar, Modificar y la acción principal (Aprobar) al final. */}
      <DialogActions sx={{ px: 1.5, py: 1, gap: 1, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
        {s && (s.estado === "espera" || s.estado === "autorizado") && (
          <Button size="small" color="error" disabled={procesando} onClick={() => accion(() => anularSolicitud(s.id), "¿Realmente desea anular el comprobante?")}>Anular</Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" color="inherit" onClick={onClose}>Cerrar</Button>
        {s && onModificar && (s.estado === "espera" || s.estado === "autorizado") && (
          <Button size="small" variant="outlined" disabled={procesando} onClick={() => onModificar(s.id)}>Modificar</Button>
        )}
        {s && s.estado === "espera" && (
          <Button size="small" variant="contained" disabled={procesando} onClick={() => accion(() => aprobarSolicitudes([s.id]), "¿Desea aprobar la solicitud?")}>Aprobar</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
